import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

// localStorage helpers for backward compatibility
function loadLocalUser() {
  try {
    const data = localStorage.getItem('orcai_user');
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function saveLocalUser(user) {
  if (user) {
    localStorage.setItem('orcai_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('orcai_user');
  }
}

// Legacy localStorage users REMOVIDO por seguranca
// Senhas eram armazenadas em texto puro no browser
// Agora toda autenticacao passa pelo Supabase Auth

function mapSupabaseUser(supaUser) {
  if (!supaUser) return null;
  const meta = supaUser.user_metadata || {};
  return {
    id: supaUser.id,
    name: meta.name || meta.full_name || meta.nome || supaUser.email?.split('@')[0] || '',
    email: supaUser.email,
    whatsapp: meta.whatsapp || '',
    analysesUsed: meta.analysesUsed || 0,
    createdAt: supaUser.created_at,
    provider: supaUser.app_metadata?.provider || 'email',
    aceita_email_marketing: meta.aceita_email_marketing || false,
    email_confirmed: !!supaUser.email_confirmed_at,
    isNewUser: meta.isNewUser || false,
  };
}

// Ensure a profile row exists in the profiles table; returns the profile's plano
async function ensureProfile(supaUser) {
  if (!supaUser) return null;
  try {
    const { data: existing } = await supabase
      .from('profiles')
      .select('id, plano')
      .eq('user_id', supaUser.id)
      .maybeSingle();

    if (existing) {
      return existing.plano;
    }

    const meta = supaUser.user_metadata || {};
    await supabase.from('profiles').insert({
      user_id: supaUser.id,
      nome: meta.name || meta.full_name || meta.nome || supaUser.email?.split('@')[0] || '',
      email: supaUser.email || '',
      whatsapp: meta.whatsapp || '',
      plano: null,
      aceita_email_marketing: meta.aceita_email_marketing || false,
    });
    return null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadLocalUser);
  const [loading, setLoading] = useState(true);

  // Sync user to localStorage
  useEffect(() => {
    saveLocalUser(user);
  }, [user]);

  // Listen to Supabase auth state changes
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const mapped = mapSupabaseUser(session.user);
          // Fetch plan from profiles table (source of truth)
          const profilePlan = await ensureProfile(session.user);
          if (profilePlan) mapped.plan = profilePlan;
          setUser(mapped);
        }
      } catch {
        // Keep existing localStorage user if Supabase fails
      }
      setLoading(false);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const mapped = mapSupabaseUser(session.user);
          setUser(mapped);
          // Fetch profile plan in background
          ensureProfile(session.user).then((profilePlan) => {
            if (profilePlan) {
              setUser((prev) => prev ? { ...prev, plan: profilePlan } : prev);
            }
          });
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Supabase signup with email confirmation
  const signUp = useCallback(async (name, email, password, extra = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            nome: name,
            whatsapp: extra.whatsapp || '',
            plan: null,
            analysesUsed: 0,
            aceita_email_marketing: extra.aceita_email_marketing || false,
            isNewUser: true,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        if (error.message.includes('already registered')) {
          return { error: 'Este email já está cadastrado. Faça login ou use outro email.' };
        }
        return { error: error.message };
      }

      // Supabase returns user with empty identities when email already exists
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        return { error: 'Este email já está cadastrado. Faça login ou use outro email.' };
      }

      // Store pending email for confirmation page
      localStorage.setItem('orcai_pending_email', email);

      // Try to create the profile immediately (user may not be confirmed yet)
      if (data.user) {
        try {
          await supabase.from('profiles').insert({
            user_id: data.user.id,
            nome: name,
            email,
            whatsapp: extra.whatsapp || '',
            plano: null,
            aceita_email_marketing: extra.aceita_email_marketing || false,
          });
        } catch {
          // Will be retried on login/callback via ensureProfile
        }
      }

      return {
        success: true,
        needsConfirmation: !data.user?.email_confirmed_at,
        user: data.user,
      };
    } catch (err) {
      return { error: 'Erro ao criar conta. Tente novamente.' };
    }
  }, []);

  // Supabase login with email/password
  const signIn = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          return { error: 'Email ou senha incorretos.' };
        }
        if (error.message.includes('Email not confirmed')) {
          localStorage.setItem('orcai_pending_email', email);
          return { error: 'Email não confirmado. Verifique sua caixa de entrada.', needsConfirmation: true };
        }
        return { error: error.message };
      }

      const mapped = mapSupabaseUser(data.user);
      // Set user immediately so login completes fast
      setUser(mapped);

      // Fetch profile plan in background (non-blocking)
      ensureProfile(data.user).then((profilePlan) => {
        if (profilePlan) {
          setUser((prev) => prev ? { ...prev, plan: profilePlan } : prev);
        }
      });

      return { success: true, isNewUser: mapped.isNewUser };
    } catch {
      return { error: 'Erro ao fazer login. Tente novamente.' };
    }
  }, []);

  // Google OAuth
  const signInWithGoogle = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://quantocustaobra.com.br/auth/callback',
          queryParams: {
            prompt: 'select_account',
          },
        },
      });
      if (error) return { error: error.message };
      return { success: true };
    } catch {
      return { error: 'Erro ao conectar com Google.' };
    }
  }, []);

  // Resend confirmation email
  const resendConfirmation = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) return { error: error.message };
      return { success: true };
    } catch {
      return { error: 'Erro ao reenviar email.' };
    }
  }, []);

  // Legacy register/login removidos — toda auth via Supabase
  // Mantemos os nomes para compatibilidade, mas redirecionam para Supabase
  const register = useCallback(async (name, email, password, extra = {}) => {
    return signUp(name, email, password, extra);
  }, [signUp]);

  const login = useCallback(async (email, password) => {
    return signIn(email, password);
  }, [signIn]);

  const logout = useCallback(async () => {
    setUser(null);
    localStorage.removeItem('orcai_user');
    localStorage.removeItem('orcai_pending_email');
    try {
      await supabase.auth.signOut();
    } catch { /* ignore */ }
  }, []);

  const updatePlan = useCallback((plan) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, plan, isNewUser: false };
      // Update Supabase user_metadata
      supabase.auth.updateUser({ data: { plan, isNewUser: false } }).catch(() => {});
      // Update profiles table
      if (prev.id) {
        supabase.from('profiles')
          .update({ plano: plan, updated_at: new Date().toISOString() })
          .eq('user_id', prev.id)
          .then(() => {});
      }
      return updated;
    });
  }, []);

  const devLogin = useCallback(() => {
    setUser({
      id: 'dev-test',
      name: 'Usuário Teste',
      email: 'teste@orcai.com',
      plan: 'pro',
      analysesUsed: 0,
      createdAt: '2026-01-01T00:00:00.000Z',
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      signIn,
      signUp,
      signInWithGoogle,
      resendConfirmation,
      logout,
      updatePlan,
      devLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
